import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ArrowUpRightIcon, QuoteIcon } from 'lucide-react';
const caseStudies = [{
  company: 'Florian Bretschneider',
  industry: 'Coaching / Appointment Setting',
  logo: 'FB',
  challenge: 'The team worked with Google Sheets at 70-100,000€ monthly cash collect. Many leads were lost, follow-up processes were chaotic, calls were forgotten.',
  solution: 'Complete Close CRM implementation with automated processes, follow-up sequences, and clear pipeline management for the entire sales team.',
  results: [{
    metric: '510k€',
    label: 'Cash Collect/Month'
  }, {
    metric: '800k€',
    label: 'Order Volume'
  }, {
    metric: '5x',
    label: 'Revenue Increase'
  }],
  quote: 'We have the CRM system with all processes and automations. We are now at 510,000 euros cash collect monthly with an order volume of over 800,000 euros. The team is much more motivated because it is simply much clearer.',
  author: 'Florian Bretschneider',
  role: 'Managing Director'
}, {
  company: 'Albert Pappelheim',
  industry: 'Sales Agency for Coaches & Consultants',
  logo: 'AP',
  challenge: 'Worked with Excel lists for over 1.5 years. Unstructured, time-consuming, only 100-150 dial attempts per day possible. No automatic recordings, difficult sales control.',
  solution: 'Close CRM with automated workflows, live call monitoring, automatic call recordings, and intelligent lead prioritization for multiple client projects.',
  results: [{
    metric: '3-4x',
    label: 'More Efficiency'
  }, {
    metric: '400',
    label: 'Dial Attempts/Day'
  }, {
    metric: '100%',
    label: 'Call Recording'
  }],
  quote: 'We work three to four times more efficiently. In one day, we easily manage 300-400 dial attempts instead of 100-150. I can listen to every conversation immediately, they are recorded automatically - that saves an immense amount of time.',
  author: 'Albert Pappelheim',
  role: 'Managing Director'
}, {
  company: 'Adrian Wettig',
  industry: 'E-Commerce / Dropshipping Mentoring',
  logo: 'AW',
  challenge: 'Worked with Google Sheets at 30,000€ revenue/month. Confusing, high administrative effort, errors due to duplicate or forgotten calls. Over 13,000 leads were barely manageable.',
  solution: 'Close CRM setup with clear processes, filters, status tracking, and comment functions. Fast implementation within a week despite complex requirements.',
  results: [{
    metric: '70k€',
    label: 'Revenue/Month'
  }, {
    metric: '2x',
    label: 'Revenue Doubled'
  }, {
    metric: '13k+',
    label: 'Leads Managed'
  }],
  quote: 'The CRM makes our lives extremely easy. I can focus much more on marketing and no longer have to deal with administrative stuff. We were able to double our revenue from 30,000€ to 70,000€.',
  author: 'Adrian Wettig',
  role: 'Managing Director'
}];
export function CaseStudies() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0c0c0c]" id="case-studies" ref={ref} data-id="element-25" data-mp="17z3o0j0rikl0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26" data-mp="1hqq4atryj9um">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-27" data-mp="1hqgd52v6ewnc">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium mb-6" data-id="element-28" data-mp="3skc79980bin6">
            Success Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-29" data-mp="2su94vjocy9d">
            Real results from real clients
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-30" data-mp="7qvrfe9a0tjs">
            See how other companies have transformed their sales with our Close
            CRM implementation.
          </p>
        </motion.div>

        <div className="space-y-8" data-id="element-31" data-mp="3b7m1c47zbqqr">
          {caseStudies.map((study, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className="rounded-2xl bg-[#1a1a1a] border border-white/5 overflow-hidden" data-id="element-32" data-mp="3g8tdsgl2he3r" data-mpi={index}>
              <div className="grid lg:grid-cols-3 gap-8 p-8" data-id="element-33" data-mp="1al2oipjopsnx">
                {/* Company info */}
                <div className="lg:border-r lg:border-white/5 lg:pr-8" data-id="element-34" data-mp="15lg8k8ch1ybf">
                  <div className="flex items-center gap-4 mb-6" data-id="element-35" data-mp="10n4x5t6j3fr3">
                    <div className="w-14 h-14 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center text-[#0ea5e9] font-bold text-xl" data-id="element-36" data-mp="10mwokdhfuwlp">
                      {study.logo}
                    </div>
                    <div data-id="element-37" data-mp="qw1voq9osyra">
                      <h3 className="text-xl font-bold text-white" data-id="element-38" data-mp="mvrjuwz7re4i">
                        {study.company}
                      </h3>
                      <p className="text-gray-500 text-sm" data-id="element-39" data-mp="hxq0sc1sfet1">{study.industry}</p>
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-40" data-mp="cwsi7ycv8ir0">
                    <div data-id="element-41" data-mp="7xz3y3pr8nps">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-42" data-mp="7zio45ej7iwu">
                        Challenge
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-43" data-mp="39omqlcn5447">{study.challenge}</p>
                    </div>
                    <div data-id="element-44" data-mp="3lg2xkbfm4xyd">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-45" data-mp="3lgmdjmlpdc9r">
                        Solution
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-46" data-mp="h1mogb40vxqc">{study.solution}</p>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="lg:border-r lg:border-white/5 lg:pr-8" data-id="element-47" data-mp="m2abymt1lwft">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6" data-id="element-48" data-mp="88ssf20cwjps">
                    Results
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-1 gap-4" data-id="element-49" data-mp="zzp4i0elekc0">
                    {study.results.map((result, i) => <div key={i} className="text-center p-4 rounded-xl bg-[#0a0a0a]" data-id="element-50" data-mp="14s8iuzl3tjhv" data-mpi={i}>
                        <div className="text-2xl lg:text-3xl font-bold text-emerald-400 break-words" data-id="element-51" data-mp="2kj1b6wyaipcb">
                          {result.metric}
                        </div>
                        <div className="text-xs text-gray-500 mt-2 break-words" data-id="element-52" data-mp="2u82uox41nov1">
                          {result.label}
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Quote */}
                <div className="flex flex-col justify-between" data-id="element-53" data-mp="21li2xvjnpaqy">
                  <div data-id="element-54" data-mp="2ghfsuw2zliuq">
                    <QuoteIcon className="w-8 h-8 text-[#0ea5e9]/30 mb-4" data-id="element-55" data-mp="2ghz825195t52" />
                    <p className="text-gray-300 italic leading-relaxed" data-id="element-56" data-mp="2bgs46h28de3o">
                      "{study.quote}"
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-3" data-id="element-57" data-mp="3mx07wlteevgx">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0ea5e9] to-[#06b6d4] flex items-center justify-center text-white font-semibold text-sm" data-id="element-58" data-mp="3myujvamozd8v">
                      {study.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div data-id="element-59" data-mp="1yz8xa6kj22pu">
                      <p className="text-white font-medium text-sm" data-id="element-60" data-mp="1yzqzdjtvfb5o">
                        {study.author}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-61" data-mp="240eqe5i3nrzw">{study.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-12 text-center" data-id="element-62" data-mp="2hyd376grvgda">
          <a href="#contact" className="group inline-flex items-center gap-2 text-[#0ea5e9] font-semibold hover:underline" data-id="element-63" data-mp="b91bomh4s3om">
            Become our next success story
            <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-64" data-mp="1bwxb8tj1cid" />
          </a>
        </motion.div>
      </div>
    </section>;
}